import { yupResolver } from '@hookform/resolvers/yup';
import type { ImageType, Retailer } from '@vizit/api';
import { Alert, AlertLevel, Button, ButtonLevel, ButtonSize } from '@vizit/atoms';
import { FormDialog, FormTextField } from '@vizit/forms';
import { PlusIcon } from '@vizit/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { type BenchmarkOption, BenchmarkSelectField, DEFAULT_BENCHMARK_OPTION_KEY } from '../benchmark-select-field';
import { VIZIT_BENCHMARK_OPTIONS, findBenchmarkOption } from '../benchmark-select-field';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';
import { ImageTypeField } from '../image-type-field';
import { collectionNameSchema } from './collection-name-schema';

const IMAGE_TYPE_OPTIONS = [
  { id: 'hero', name: 'Hero' },
  { id: 'carousel', name: 'Carousel' },
] as const;

/** The raw form values; `benchmark` holds a benchmark option key such as `vizit:amazon`. */
type CollectionsFormValues = {
  name: string;
  category: string;
  imageType: ImageType;
  benchmark: string;
};

/** What `onSubmit` receives: the form values with the benchmark key resolved to its option. */
type CollectionsFormSchema = {
  name: string;
  /** Empty when custom benchmarks are enabled and the user skipped the category. */
  category: string;
  imageType: ImageType;
  /** Only present when custom benchmarks are enabled. */
  benchmark?: BenchmarkOption;
};

const EMPTY_FORM: CollectionsFormValues = {
  name: '',
  category: '',
  imageType: undefined as unknown as ImageType,
  benchmark: DEFAULT_BENCHMARK_OPTION_KEY,
};

interface CollectionsDialogProps {
  /**
   * Error message from submission attempt
   *
   * @optional
   * @type {string | null}
   */
  submissionErrorMessage?: string | null;
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<CollectionsFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Default values for the form; `benchmark` is a benchmark option key and defaults to Amazon
   *
   * @optional
   * @type {Partial<CollectionsFormValues>}
   */
  defaultValues?: Partial<CollectionsFormValues>;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * When true, the "Hero" image type is omitted from the image-type picker, leaving only "Carousel".
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  hideHeroOption?: boolean;
  /**
   * When true, the product category becomes optional and a required Benchmark field
   * (the retailer benchmarks plus the workspace's own sets) is added above the image type,
   * defaulting to Amazon. Picking a custom set locks the image type to the set's. Off, the
   * dialog is unchanged.
   *
   * @optional
   * @type {boolean}
   * @default false
   */
  showBenchmarkField?: boolean;
  /**
   * The workspace's custom benchmarks, in every scope; only used with `showBenchmarkField`
   *
   * @optional
   * @type {BenchmarkOption[]}
   */
  customBenchmarkOptions?: BenchmarkOption[];
  /**
   * True while the custom benchmarks are loading
   *
   * @optional
   * @type {boolean}
   */
  isBenchmarkOptionsLoading?: boolean;
  /**
   * Shown under the Benchmark field when the custom benchmarks failed to load
   *
   * @optional
   * @type {string | null}
   */
  benchmarkOptionsErrorMessage?: string | null;
  /**
   * The Vizit retailers the picked category and image type can be scored against; the others are listed disabled,
   * saying the retailer has no benchmark for the category. Undefined while that is not known (no category or image
   * type picked, still loading, or the lookup failed), which disables none: the create still refuses a bad pick.
   *
   * @optional
   * @type {Retailer[]}
   */
  scorableRetailers?: Retailer[];
  /**
   * Called with the picked category (empty when skipped) and image type whenever either changes, so the caller can
   * look up `scorableRetailers`
   *
   * @optional
   */
  onCategoryOrImageTypeChange?: (selection: { category: string; imageType?: ImageType }) => void;
  /**
   * Callback function to handle the collection creation
   */
  onSubmit: (data: CollectionsFormSchema) => Promise<void>;
  /**
   * Callback function when dialog open state changes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * Dialog component for creating a new collection
 * Allows users to enter collection details including name, product category, and image type
 *
 * @param props - The component props
 * @param props.onSubmit - Callback function when form is submitted
 *
 * @example
 * <CollectionsDialog onSubmit={(data) => console.log(data)} />
 */
function CollectionsDialog(props: CollectionsDialogProps) {
  const {
    submissionErrorMessage = null,
    productCategoryComboBoxProps,
    defaultValues: defaultValuesProp,
    isOpen,
    hideTrigger = false,
    triggerButtonText = 'Create collection',
    useIcon = false,
    hideHeroOption = false,
    showBenchmarkField = false,
    customBenchmarkOptions,
    isBenchmarkOptionsLoading = false,
    benchmarkOptionsErrorMessage = null,
    scorableRetailers,
    onCategoryOrImageTypeChange,
    onSubmit,
    onOpenChange,
  } = props;

  const defaultValues = React.useMemo<CollectionsFormValues>(
    () => ({ ...EMPTY_FORM, ...defaultValuesProp }),
    [defaultValuesProp],
  );

  const imageTypeOptions = hideHeroOption
    ? IMAGE_TYPE_OPTIONS.filter((option) => option.id !== 'hero')
    : IMAGE_TYPE_OPTIONS;

  // Every custom set is offered, whatever its image type, as long as the dialog offers that type. A retailer the
  // picked category has no benchmark under is listed disabled, with the reason.
  const benchmarkOptions = React.useMemo(
    () => [
      ...VIZIT_BENCHMARK_OPTIONS.map((option) =>
        scorableRetailers && option.retailer && !scorableRetailers.includes(option.retailer)
          ? { ...option, disabled: true, disabledReason: `No ${option.name} benchmark for this category` }
          : option,
      ),
      ...(customBenchmarkOptions ?? []).filter((option) => !(hideHeroOption && option.scope === 'hero')),
    ],
    [scorableRetailers, customBenchmarkOptions, hideHeroOption],
  );

  const categoryIds = productCategoryComboBoxProps.items.map((item) => item.id);

  const formSchema = yup.object().shape({
    name: collectionNameSchema,
    // The combo box only ever sets an id it listed, and its list is a filtered page, so the
    // optional category is not checked against the page: a filter change must not invalidate a pick. Its clear button
    // sets null, which counts as skipped like the untouched ''.
    category: showBenchmarkField
      ? yup
          .string()
          .defined()
          .transform((value) => value ?? '')
      : yup.string().oneOf(categoryIds, 'Product category is required').required('Product category is required'),
    // A custom set only scores its own image type. The check sits here, not on the benchmark, because picking a set
    // corrects the image type a render later: on the benchmark it would fail against the old type, and the delayed
    // error would surface after the correction cleared it. It is only a guard behind that lock, which always sets the
    // set's type, so it never fails in use.
    imageType: yup
      .string()
      .oneOf(imageTypeOptions.map((option) => option.id))
      .required('Image type is required')
      .test('benchmark-scope', 'Image type is set by the benchmark', function matchesBenchmark(value) {
        if (!showBenchmarkField) return true;
        const scope = findBenchmarkOption(benchmarkOptions, this.parent.benchmark)?.scope;
        return !scope || scope === value;
      }),
    benchmark: showBenchmarkField
      ? yup
          .string()
          .required('Benchmark is required')
          .test('scorable', 'Choose a benchmark from the list', function isScorable(value) {
            const option = findBenchmarkOption(benchmarkOptions, value);
            if (option?.disabledReason) return this.createError({ message: option.disabledReason });
            return !!option && !option.disabled;
          })
      : yup.string().defined(),
  });

  const formMethods = useForm<CollectionsFormValues>({
    resolver: yupResolver(formSchema),
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset, clearErrors, watch, getValues, setValue, trigger } = formMethods;

  // A failed create belongs to the whole form, not to one field: on a field it would keep the form invalid until that
  // field was edited, even when the fix was another field. Any change hides it, so the next attempt can be made, and
  // every settled attempt shows the latest message again, even when it repeats the one hidden.
  const [isFormErrorHidden, setIsFormErrorHidden] = React.useState(false);
  const formError = isFormErrorHidden ? null : submissionErrorMessage;
  // Counts the changes, so an attempt that settles after the form changed under it leaves its error hidden: it is
  // about values the form no longer has.
  const formChanges = React.useRef(0);

  // The callback also hears form-state updates, such as the submit settling or a field's validation; only a field's
  // value change carries both its name and the values.
  React.useEffect(() => {
    const subscription = watch((_, { name, values }) => {
      if (name && values) {
        formChanges.current += 1;
        setIsFormErrorHidden(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const category = watch('category');
  const imageType = watch('imageType');

  React.useEffect(() => {
    onCategoryOrImageTypeChange?.({ category: category ?? '', imageType: imageType || undefined });
  }, [category, imageType, onCategoryOrImageTypeChange]);

  // A pick still listed is checked again when the options change, e.g. a Walmart picked before the category's answer
  // came back, so the reason it cannot be used shows on the field, and goes once the category changes.
  React.useEffect(() => {
    if (showBenchmarkField && findBenchmarkOption(benchmarkOptions, getValues('benchmark'))) void trigger('benchmark');
  }, [showBenchmarkField, benchmarkOptions, getValues, trigger]);

  const selectedBenchmark = showBenchmarkField ? findBenchmarkOption(benchmarkOptions, watch('benchmark')) : undefined;
  // A custom set only scores its own image type, so picking one sets and locks the collection's. Switching back to a
  // retailer benchmark unlocks the field but keeps that type, not the one picked before the custom set.
  const lockedImageType = selectedBenchmark?.kind === 'custom' ? (selectedBenchmark.scope ?? undefined) : undefined;

  React.useEffect(() => {
    if (lockedImageType && getValues('imageType') !== lockedImageType) {
      setValue('imageType', lockedImageType, { shouldValidate: true });
    }
  }, [lockedImageType, getValues, setValue]);

  // Falls back to Amazon when the pick left the list: after a refetch (deleted, or not found), or because the Hero type
  // was hidden with a hero set picked. A Hero type left behind is cleared too, since no card could change it.
  React.useEffect(() => {
    if (hideHeroOption && getValues('imageType') === 'hero') {
      setValue('imageType', EMPTY_FORM.imageType, { shouldValidate: true });
    }
    if (!showBenchmarkField) return;
    if (!findBenchmarkOption(benchmarkOptions, getValues('benchmark'))) {
      setValue('benchmark', DEFAULT_BENCHMARK_OPTION_KEY, { shouldValidate: true });
    }
  }, [showBenchmarkField, hideHeroOption, benchmarkOptions, getValues, setValue]);

  const handleOnResetForm = () => {
    clearErrors();
    formChanges.current += 1;
    setIsFormErrorHidden(true);

    reset(defaultValues);
  };

  const handleFormSubmit = async (data: CollectionsFormValues) => {
    const { benchmark, ...values } = data;
    const changesAtSubmit = formChanges.current;

    try {
      await onSubmit(
        showBenchmarkField ? { ...values, benchmark: findBenchmarkOption(benchmarkOptions, benchmark) } : values,
      );
    } finally {
      if (formChanges.current === changesAtSubmit) setIsFormErrorHidden(false);
    }
  };

  return (
    <FormDialog
      formMethods={formMethods}
      formName="collections"
      isOpen={isOpen}
      onSubmit={handleSubmit(handleFormSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={onOpenChange}
      data-testid="collection-dialog"
    >
      {!hideTrigger && (
        <FormDialog.Trigger>
          <Button
            level={ButtonLevel.Primary}
            size={ButtonSize.Lg}
            aria-label={triggerButtonText}
            iconStart={useIcon ? undefined : <PlusIcon size={20} />}
            data-testid="collection-trigger"
          >
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        </FormDialog.Trigger>
      )}
      <FormDialog.Title>Create a collection</FormDialog.Title>
      <FormDialog.Subtitle>
        {showBenchmarkField
          ? 'Name your collection and choose a benchmark: Amazon, Walmart, or one of your own.'
          : 'Name your collection and choose product category to benchmark your assets.'}
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <div className="flex flex-col gap-10">
          <FormTextField
            control={control}
            name="name"
            label="Collection name"
            placeholder="Enter collection name"
            isRequired
            className="min-h-0"
            inputProps={{ 'data-testid': 'collection-name-input' }}
          />
          <FormProductCategoryComboBox
            control={control}
            name="category"
            label="Product category"
            isRequired={!showBenchmarkField}
            description={showBenchmarkField ? 'Can be done later' : undefined}
            menuTrigger="input"
            className="min-h-0"
            data-testid="collection-category"
            {...productCategoryComboBoxProps}
          />
          {showBenchmarkField && (
            <BenchmarkSelectField
              control={control}
              name="benchmark"
              options={benchmarkOptions}
              isLoading={isBenchmarkOptionsLoading}
              loadErrorMessage={benchmarkOptionsErrorMessage ?? undefined}
              isRequired
            />
          )}
          <ImageTypeField
            control={control}
            name="imageType"
            hideHeroOption={hideHeroOption}
            lockedReason={lockedImageType ? 'Set by the benchmark' : undefined}
          />
          {formError && (
            <Alert level={AlertLevel.Error} border={false} className="p-0">
              <Alert.Description>{formError}</Alert.Description>
            </Alert>
          )}
        </div>
      </FormDialog.FormFields>
      <FormDialog.SubmitButton data-testid="collection-submit">Create collection</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CollectionsDialog };
export type { CollectionsFormSchema, CollectionsFormValues };
